// The 3 things that matter most this week (docs/specs/overview.md). Each person
// keeps their own; RLS returns only the reader's rows.

import { useEffect, useState } from 'react';
import {
  clearPriority,
  mondayOf,
  savePriority,
  usePriorities,
  type WeeklyPriority,
} from '../../lib/data';
import { useToast } from '../../ui/kit';

const STATUSES: { id: WeeklyPriority['status']; label: string }[] = [
  { id: 'todo', label: 'To do' },
  { id: 'doing', label: 'In progress' },
  { id: 'done', label: 'Done' },
  { id: 'dropped', label: 'Dropped' },
];

function weekLabel(monday: string) {
  const a = new Date(`${monday}T12:00:00`);
  const b = new Date(a.getTime() + 6 * 86_400_000);
  const f = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${f(a)} to ${f(b)}`;
}

export function Priorities() {
  const week = mondayOf();
  const prev = mondayOf(new Date(Date.now() - 7 * 86_400_000));
  const q = usePriorities(week, prev);
  const rows = q.data ?? [];
  const now = rows.filter((r) => r.week_start === week);
  const last = rows.filter((r) => r.week_start === prev);
  const done = now.filter((r) => r.status === 'done').length;

  return (
    <section className="card prio" aria-label="Weekly priorities">
      <div className="chead">
        <span className="dot" />
        <span className="lbl g">Top 3 this week</span>
        <span className="viewall" style={{ cursor: 'default' }}>
          {weekLabel(week)}
          {now.length ? `, ${done} of ${now.length} done` : ''}
        </span>
      </div>
      <div className="prio-list">
        {[1, 2, 3].map((rank) => (
          <Slot
            key={`${week}-${rank}`}
            week={week}
            rank={rank}
            row={now.find((r) => r.rank === rank)}
          />
        ))}
      </div>
      {last.length > 0 && (
        <details className="prio-last">
          <summary>
            Last week: {last.filter((r) => r.status === 'done').length} of {last.length} done
          </summary>
          <ul>
            {last.map((r) => (
              <li key={r.id} className={`ps-${r.status}`}>
                <b>{r.rank}.</b> {r.title}{' '}
                <span className="pill sm gr">{STATUSES.find((s) => s.id === r.status)?.label}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Slot({
  week,
  rank,
  row,
}: {
  week: string;
  rank: number;
  row: WeeklyPriority | undefined;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(row?.title ?? '');
  useEffect(() => setTitle(row?.title ?? ''), [row?.title]);

  async function run(p: Promise<unknown>) {
    try {
      await p;
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  function commit() {
    const t = title.trim();
    if (t === (row?.title ?? '')) return;
    if (!t && row) return void run(clearPriority(row.id));
    if (t) void run(savePriority(row?.id ?? null, week, rank, { title: t }));
  }

  return (
    <div className={`prio-slot ps-${row?.status ?? 'empty'}`}>
      <span className="prio-n" aria-hidden>
        {rank}
      </span>
      <input
        type="text"
        aria-label={`Priority ${rank}`}
        placeholder={rank === 1 ? 'Most important this week' : 'Add a priority'}
        value={title}
        maxLength={200}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      {row && (
        <select
          aria-label={`Status of priority ${rank}`}
          value={row.status}
          onChange={(e) =>
            void run(
              savePriority(row.id, week, rank, {
                status: e.target.value as WeeklyPriority['status'],
              }),
            )
          }
        >
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
